import { notify } from '@/lib/toast';
import { openPrintWindow } from '@/lib/print-document';
import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { BookOpen, Plus, DollarSign, ArrowLeft, Banknote, CreditCard, Smartphone, Building2, FileText, Printer, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuthStore } from '@/stores/auth.store';
import { formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Dialog } from 'primereact/dialog';
import { BankAccountField } from '@/components/pos/BankAccountField';
import { ChequeFields, chequeFormToPayload, emptyChequeForm, isChequeFormComplete, type ChequeForm } from '@/components/pos/ChequeFields';
import { RecordLink } from '@/components/records/RecordLink';
import { PaymentReceiptView, type KhataPaymentReceipt } from '@/components/pos/PaymentReceiptView';
import { effectiveBankAccountId, rememberBankAccountId, type BankAccountOption } from '@/lib/bank-accounts';
import { paymentMethodLabel } from '@/lib/payment-labels';

/** i18n key (customers ns) for each tender label; unknown methods fall back to the shared English label. */
const METHOD_LABEL_KEYS: Record<string, string> = {
  CASH: 'methodCash',
  CARD: 'methodCard',
  MOBILE_WALLET: 'methodWallet',
  BANK_TRANSFER: 'methodBank',
  CHEQUE: 'methodCheque',
  QR_CODE: 'methodQr',
  CREDIT: 'methodCredit',
  GIFT_CARD: 'methodGiftCard',
};

interface LedgerEntryRow {
  id: string;
  createdAt: string;
  type: string;
  notes?: string | null;
  reference?: string | null;
  paymentMethod?: string | null;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  sale?: { id?: string; invoiceNumber: string } | null;
  /** POS-I135: invoices this payment cleared (FIFO or manual pick). */
  allocations?: Array<{ saleId: string | null; invoiceNumber: string | null; amount: number }>;
}

/** POS-I135: an unpaid credit invoice with its age. */
interface OpenInvoiceRow {
  chargeEntryId: string;
  saleId: string | null;
  invoiceNumber: string | null;
  chargedAt: string;
  amount: number;
  paid: number;
  open: number;
  ageDays: number;
}

interface AgingData {
  current: number;
  days31to60: number;
  over60: number;
  otherBalance: number;
  total: number;
}

function escapeHtml(v: string): string {
  return v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function csvCell(v: string | number): string {
  const x = String(v ?? '');
  return /[",\n]/.test(x) ? '"' + x.replace(/"/g, '""') + '"' : x;
}

const PAYMENT_METHODS = [
  { value: 'CASH',          labelKey: 'methodCash',   icon: <Banknote className="h-5 w-5" /> },
  { value: 'CARD',          labelKey: 'methodCard',   icon: <CreditCard className="h-5 w-5" /> },
  { value: 'MOBILE_WALLET', labelKey: 'methodWallet', icon: <Smartphone className="h-5 w-5" /> },
  { value: 'BANK_TRANSFER', labelKey: 'methodBank',   icon: <Building2 className="h-5 w-5" /> },
  { value: 'CHEQUE',        labelKey: 'methodCheque', icon: <FileText className="h-5 w-5" /> },
] as const;

type PayMethod = typeof PAYMENT_METHODS[number]['value'];

export function CustomerLedgerPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation('customers');
  const methodLabel = (method: string): string => {
    const key = METHOD_LABEL_KEYS[method];
    return key ? t(key) : paymentMethodLabel(method);
  };
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('customers:write') || hasPermission('customers:*');
  const canUseBank = hasPermission('bank:*') || hasPermission('bank:read');
  const canUseCheques = hasPermission('cheques:*');

  const [page, setPage] = useState(1);
  // POS-I142: the Khata list's Collect button opens this page with ?pay=1 so Record Payment is already open.
  const [searchParams] = useSearchParams();
  const [paymentOpen, setPaymentOpen] = useState(() => searchParams.get('pay') === '1' && canWrite);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [payForm, setPayForm] = useState<{ amount: string; method: PayMethod; reference: string; bankAccountId: string; notes: string; cheque: ChequeForm }>({
    amount: '', method: 'CASH', reference: '', bankAccountId: '', notes: '', cheque: emptyChequeForm(),
  });
  /** POS-I135: optional manual invoice pick (saleId -> amount text); the rest of the payment goes oldest-first. */
  const [invoicePick, setInvoicePick] = useState<Record<string, string>>({});
  const [receipt, setReceipt] = useState<KhataPaymentReceipt | null>(null);
  const [adjForm, setAdjForm] = useState<{ amount: string; notes: string; direction: 'reduce' | 'increase' }>({ amount: '', notes: '', direction: 'reduce' });

  const qc = useQueryClient();
  const handleLedgerUpdate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['customer-ledger'] });
    qc.invalidateQueries({ queryKey: ['customer-credit'] });
  }, [qc]);
  useSocketEvent('sale.created', handleLedgerUpdate);
  useSocketEvent('sale.voided', handleLedgerUpdate);
  useSocketEvent('sale.refunded', handleLedgerUpdate);
  useSocketEvent('customer.updated', handleLedgerUpdate);

  const { data: customer } = useQuery({
    queryKey: ['customer', id],
    queryFn: () => api.get(`/customers/${id}`).then(r => r.data),
    enabled: !!id,
  });

  const { data, isLoading } = useQuery({
    queryKey: ['customer-ledger', id, page],
    queryFn: () => api.get(`/customers/${id}/ledger`, { params: { page, limit: 20 } }).then(r => r.data),
    enabled: !!id,
  });

  // Cashier-safe options endpoint (id/name only) — no bank:read needed to pick a receiving account.
  const { data: bankAccounts = [] } = useQuery<BankAccountOption[]>({
    queryKey: ['bank-accounts-options'],
    queryFn: () => api.get('/bank-accounts/options').then(r => r.data as BankAccountOption[]),
    enabled: paymentOpen,
    staleTime: 60_000,
  });

  const { data: activePlans } = useQuery<{ total: number }>({
    queryKey: ['customer-active-installments', id],
    queryFn: () => api.get('/installments', { params: { customerId: id, status: 'ACTIVE', limit: 1 } }).then(r => r.data),
    enabled: !!id && paymentOpen,
  });
  const hasActiveInstallments = (activePlans?.total ?? 0) > 0;
  const openInvoices: OpenInvoiceRow[] = data?.openInvoices ?? [];
  const aging: AgingData | undefined = data?.aging;
  const pickedAllocations = openInvoices
    .filter((i) => i.saleId && parseFloat(invoicePick[i.saleId] ?? '') > 0)
    .map((i) => ({ saleId: i.saleId as string, amount: parseFloat(invoicePick[i.saleId as string]) }));
  const pickedTotal = pickedAllocations.reduce((sum, a) => sum + a.amount, 0);
  const payAmountNum = parseFloat(payForm.amount) || 0;
  const pickExceeds = pickedTotal > payAmountNum + 0.001 || openInvoices.some((i) => i.saleId && parseFloat(invoicePick[i.saleId] ?? '') > i.open + 0.001);

  const payMutation = useMutation({
    mutationFn: () => api.post(`/customers/${id}/ledger/payment`, {
      amount: parseFloat(payForm.amount),
      paymentMethod: payForm.method,
      bankAccountId: effectiveBankAccountId(payForm.method, payForm.bankAccountId, bankAccounts),
      reference: payForm.method !== 'CASH' && payForm.method !== 'CHEQUE' ? (payForm.reference.trim() || undefined) : undefined,
      cheque: payForm.method === 'CHEQUE' ? chequeFormToPayload(payForm.cheque) : undefined,
      notes: payForm.notes || undefined,
      allocations: pickedAllocations.length ? pickedAllocations : undefined,
    }).then(r => r.data as { receipt: KhataPaymentReceipt }),
    onSuccess: (res) => {
      toast.success(t('paymentRecorded'));
      const usedBank = effectiveBankAccountId(payForm.method, payForm.bankAccountId, bankAccounts);
      if (usedBank) rememberBankAccountId(usedBank);
      setReceipt(res.receipt);
      qc.invalidateQueries({ queryKey: ['cheques'] });
      qc.invalidateQueries({ queryKey: ['customer-ledger', id] });
      qc.invalidateQueries({ queryKey: ['customer', id] });
      qc.invalidateQueries({ queryKey: ['khata-outstanding'] });
      qc.invalidateQueries({ queryKey: ['customer-active-installments', id] });
      qc.invalidateQueries({ queryKey: ['installments'] });
      setPaymentOpen(false);
      setPayForm({ amount: '', method: 'CASH', reference: '', bankAccountId: '', notes: '', cheque: emptyChequeForm() });
      setInvoicePick({});
    },
    onError: (err) => notify.error(err, t('failedToRecordPayment')),
  });

  const adjMutation = useMutation({
    mutationFn: () => api.post(`/customers/${id}/ledger/adjust`, {
      // POS-I134: the dialog takes a positive amount and a direction; the API keeps its signed amount.
      amount: (adjForm.direction === 'reduce' ? -1 : 1) * Math.abs(parseFloat(adjForm.amount)),
      type: 'ADJUSTMENT',
      notes: adjForm.notes || undefined,
    }),
    onSuccess: () => {
      toast.success(t('balanceAdjusted'));
      qc.invalidateQueries({ queryKey: ['customer-ledger', id] });
      qc.invalidateQueries({ queryKey: ['customer', id] });
      qc.invalidateQueries({ queryKey: ['khata-outstanding'] });
      setAdjustOpen(false);
      setAdjForm({ amount: '', notes: '', direction: 'reduce' });
    },
    onError: (err) => notify.error(err, t('failedToAdjust')),
  });

  const entryTypeLabel = (type: string): string =>
    type === 'CHARGE' ? t('charge') : type === 'PAYMENT' ? t('payment') : type === 'ADJUSTMENT' ? t('typeAdjustment') : type;

  const [statementBusy, setStatementBusy] = useState(false);
  /** Shop identity for the statement header; every lookup is best effort (permissions differ per role). */
  const loadStatementShop = async (): Promise<{ name: string; address: string; phone: string; ntn: string }> => {
    const out = { name: '', address: '', phone: '', ntn: '' };
    try {
      const tn = (await api.get('/tenant')).data;
      out.name = tn?.name ?? '';
      out.address = tn?.fbrAddress ?? '';
      out.ntn = tn?.fbrNtn ?? '';
    } catch { /* header falls back to whatever else is available */ }
    try {
      const raw = (await api.get('/locations')).data;
      const list: Array<{ address?: string; city?: string; phone?: string; isDefault?: boolean }> = raw?.data ?? raw ?? [];
      const loc = list.find((l) => l.isDefault) ?? list[0];
      if (loc) {
        const addr = [loc.address, loc.city].filter(Boolean).join(', ');
        if (addr) out.address = addr;
        out.phone = loc.phone ?? '';
      }
    } catch { /* no locations access */ }
    return out;
  };
  /** POS-I132: customer statement (opening balance, charges, payments, closing balance) as a print view or CSV. */
  const exportStatement = async (mode: 'print' | 'csv') => {
    if (!id) return;
    setStatementBusy(true);
    try {
      const res = await api.get(`/customers/${id}/ledger`, { params: { page: 1, limit: 5000 } });
      const all: LedgerEntryRow[] = [...(res.data?.entries ?? [])].reverse(); // oldest first
      const name: string = res.data?.customer?.name ?? customer?.name ?? 'Customer';
      const opening = all.length ? Number(all[0].balanceBefore) : 0;
      const closing = Number(res.data?.customer?.outstandingBalance ?? (all.length ? all[all.length - 1].balanceAfter : 0));
      const isDebit = (e: LedgerEntryRow) => e.type === 'CHARGE' || (e.type === 'ADJUSTMENT' && e.balanceAfter > e.balanceBefore);
      const detail = (e: LedgerEntryRow) => [
        e.sale?.invoiceNumber ?? '',
        e.notes ?? '',
        e.paymentMethod ? methodLabel(e.paymentMethod) : '',
        e.reference ? `${t('refLabel')}: ${e.reference}` : '',
        e.type === 'PAYMENT' && e.allocations?.length ? `Paid ${e.allocations.map((a) => `${a.invoiceNumber ?? '-'} (${a.amount})`).join(', ')}` : '',
      ].filter(Boolean).join(' · ');
      const ag: AgingData | undefined = res.data?.aging;
      const agingLines: Array<[string, number]> = ag && closing > 0
        ? [['Aging 0-30 days', ag.current], ['Aging 31-60 days', ag.days31to60], ['Aging 60+ days', ag.over60], ['Not on an invoice', ag.otherBalance]]
        : [];
      const today = new Date().toISOString().slice(0, 10);
      // POS-I136: shop header. /tenant gives the name to everyone (address/NTN only with settings:update);
      // the location (address, phone) is the one the receipts use, so a role without locations access gets the name only.
      const shop = await loadStatementShop();
      const shopLines = [shop.address, shop.phone ? `Tel: ${shop.phone}` : '', shop.ntn ? `NTN: ${shop.ntn}` : ''].filter(Boolean);
      const custPhone: string = res.data?.customer?.phone ?? customer?.phone ?? '';
      const custAddress: string = res.data?.customer?.address ?? customer?.address ?? '';
      const custLine = [custPhone ? `Phone: ${custPhone}` : '', custAddress].filter(Boolean).join(' · ');
      if (mode === 'csv') {
        const lines = [
          ...(shop.name ? [[shop.name].map(csvCell).join(',')] : []),
          ...(shopLines.length ? [[shopLines.join(' | ')].map(csvCell).join(',')] : []),
          [`Account statement: ${name}`].map(csvCell).join(','),
          ...(custLine ? [[custLine].map(csvCell).join(',')] : []),
          ['Date', 'Type', 'Details', 'Charge', 'Payment', 'Balance'].join(','),
          ['', 'Opening balance', '', '', '', opening].map(csvCell).join(','),
          ...all.map((e) => [
            new Date(e.createdAt).toLocaleDateString(), entryTypeLabel(e.type), detail(e),
            isDebit(e) ? e.amount : '', isDebit(e) ? '' : e.amount, e.balanceAfter,
          ].map(csvCell).join(',')),
          ['', 'Closing balance', '', '', '', closing].map(csvCell).join(','),
          ...agingLines.map(([label, v]) => ['', label, '', '', '', v].map(csvCell).join(',')),
        ];
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `statement-${name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${today}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
        return;
      }
      const rowsHtml = all.map((e) => `<tr><td>${escapeHtml(new Date(e.createdAt).toLocaleDateString())}</td><td>${escapeHtml(entryTypeLabel(e.type))}</td><td>${escapeHtml(detail(e))}</td><td class="r">${isDebit(e) ? escapeHtml(formatCurrency(e.amount)) : ''}</td><td class="r">${isDebit(e) ? '' : escapeHtml(formatCurrency(e.amount))}</td><td class="r">${escapeHtml(formatCurrency(e.balanceAfter))}</td></tr>`).join('');
      // POS-I240: shared printable-document builder (Powered by footer on every page).
      if (!openPrintWindow({ title: `Statement - ${name}`, bodyHtml: `${shop.name ? `<div class="shop">${escapeHtml(shop.name)}</div>` : ''}${shopLines.length ? `<div class="sub">${escapeHtml(shopLines.join(' | '))}</div>` : ''}<h1>Account statement: ${escapeHtml(name)}</h1>${custLine ? `<div class="sub">${escapeHtml(custLine)}</div>` : ''}<div class="sub">Printed ${escapeHtml(new Date().toLocaleDateString())}</div>
<table><thead><tr><th>Date</th><th>Type</th><th>Details</th><th class="r">Charge</th><th class="r">Payment</th><th class="r">Balance</th></tr></thead><tbody>
<tr class="tot"><td colspan="5">Opening balance</td><td class="r">${escapeHtml(formatCurrency(opening))}</td></tr>${rowsHtml}
<tr class="tot"><td colspan="5">Closing balance (amount due)</td><td class="r">${escapeHtml(formatCurrency(closing))}</td></tr>${agingLines.map(([label, v]) => `<tr><td colspan="5">${escapeHtml(label)}</td><td class="r">${escapeHtml(formatCurrency(v))}</td></tr>`).join('')}</tbody></table>
`, extraCss: '.shop{font-size:18px;font-weight:700}h1{margin-top:12px}' })) { toast.error('Allow pop-ups to print the statement.'); return; }
    } catch (err) {
      notify.error(err, 'Could not prepare the statement.');
    } finally {
      setStatementBusy(false);
    }
  };

  const entries: LedgerEntryRow[] = data?.entries ?? [];
  const total = data?.total ?? 0;
  const balance = data?.customer?.outstandingBalance ?? customer?.outstandingBalance ?? 0;
  const adjAmountNum = Math.abs(parseFloat(adjForm.amount) || 0);
  const adjExceedsBalance = adjForm.direction === 'reduce' && adjAmountNum > balance;
  const adjNewBalance = adjForm.direction === 'reduce' ? balance - adjAmountNum : balance + adjAmountNum;

  return (
    <div className="space-y-6">
      {!embedded && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/customers/khata" className="hover:text-foreground flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" /> {t('khataBack')}
          </Link>
          <span>/</span>
          <span>{customer?.name}</span>
        </div>
      )}

      <PageHeader
        title={embedded ? t('ledgerTitle') : t('customerLedgerTitle', { name: customer?.name ?? t('customerFallback') })}
        actions={(
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => void exportStatement('print')} disabled={statementBusy}>
              <Printer className="h-4 w-4 me-2" /> Print statement
            </Button>
            <Button variant="outline" onClick={() => void exportStatement('csv')} disabled={statementBusy}>
              <Download className="h-4 w-4 me-2" /> Export CSV
            </Button>
            {canWrite && (<>
            <Button onClick={() => setPaymentOpen(true)}>
              <DollarSign className="h-4 w-4 me-2" /> {t('recordPayment')}
            </Button>
            <Button variant="secondary" onClick={() => setAdjustOpen(true)}>
              <Plus className="h-4 w-4 me-2" /> {t('adjustAction')}
            </Button>
            </>)}
          </div>
        )}
      />

      {/* Balance summary */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground">{t('outstandingBalance')}</p>
            <p className={`text-2xl font-bold ${Number(balance) > 0 ? 'text-destructive-text' : Number(balance) < 0 ? 'text-warning-text' : 'text-success-text'}`}>
              {formatCurrency(Math.abs(balance))}
            </p>
            <p className="text-xs text-muted-foreground">{balance > 0 ? t('customerOwes') : balance < 0 ? t('storeOwes') : t('settledLabel')}</p>
          </CardContent>
        </Card>
        {customer?.creditLimit && (
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">{t('creditLimit')}</p>
              <p className="text-2xl font-bold">{formatCurrency(customer.creditLimit)}</p>
              <p className="text-xs text-muted-foreground">{t('availableCredit', { amount: formatCurrency(Math.max(0, customer.creditLimit - balance)) })}</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* POS-I135: debt aging and unpaid invoices */}
      {aging && balance > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">{t('agingTitle', { defaultValue: 'Debt aging' })}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="ledger-aging">
              {([
                ['aging0to30', '0-30 days', aging.current],
                ['aging31to60', '31-60 days', aging.days31to60],
                ['aging60plus', '60+ days', aging.over60],
                ['agingOther', 'Not on an invoice', aging.otherBalance],
              ] as const).map(([key, label, value]) => (
                <div key={key} className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">{t(key, { defaultValue: label })}</p>
                  <p className={`text-lg font-bold ${key === 'aging60plus' && value > 0 ? 'text-destructive-text' : ''}`}>{formatCurrency(value)}</p>
                </div>
              ))}
            </div>
            {openInvoices.length > 0 && (
              <div className="overflow-x-auto">
                <Table mobileCards>
                  <THead>
                    <Tr hover={false}>
                      <Th>{t('openInvoice', { defaultValue: 'Unpaid invoice' })}</Th>
                      <Th>{t('colDate')}</Th>
                      <Th className="text-end">{t('invoiceAge', { defaultValue: 'Age (days)' })}</Th>
                      <Th className="text-end">{t('invoiceTotal', { defaultValue: 'Invoice total' })}</Th>
                      <Th className="text-end">{t('invoiceUnpaid', { defaultValue: 'Unpaid' })}</Th>
                    </Tr>
                  </THead>
                  <TBody>
                    {openInvoices.map((i) => (
                      <Tr key={i.chargeEntryId}>
                        <Td className="font-mono text-xs" label={t('openInvoice', { defaultValue: 'Unpaid invoice' })}>
                          {i.invoiceNumber ? <RecordLink type="invoice" id={i.saleId} label={i.invoiceNumber} variant="mono" className="text-xs" /> : '—'}
                        </Td>
                        <Td className="text-xs text-muted-foreground" label={t('colDate')}>{new Date(i.chargedAt).toLocaleDateString()}</Td>
                        <Td className="text-end" label={t('invoiceAge', { defaultValue: 'Age (days)' })}>{i.ageDays}</Td>
                        <Td className="text-end" label={t('invoiceTotal', { defaultValue: 'Invoice total' })}>{formatCurrency(i.amount)}</Td>
                        <Td className="text-end font-medium" label={t('invoiceUnpaid', { defaultValue: 'Unpaid' })}>{formatCurrency(i.open)}</Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Ledger entries table */}
      <Card>
        <CardHeader><CardTitle className="text-base">{t('transactionHistory')}</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? <SkeletonTable rows={8} cols={5} /> : entries.length === 0 ? (
            <EmptyState icon={BookOpen} title={t('noTransactionsYet')} description={t('noTransactionsDesc')} />
          ) : (
            <div className="overflow-x-auto">
              <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('colDate')}</Th>
                    <Th>{t('colType')}</Th>
                    <Th>{t('notes')}</Th>
                    <Th className="text-end">{t('charge')}</Th>
                    <Th className="text-end">{t('payment')}</Th>
                    <Th className="text-end">{t('colBalance')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {entries.map((e) => (
                    <Tr key={e.id}>
                      <Td className="text-xs text-muted-foreground" label={t('colDate')}>{new Date(e.createdAt).toLocaleString()}</Td>
                      <Td label={t('colType')}>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          e.type === 'CHARGE' ? 'bg-destructive/10 text-destructive-text' :
                          e.type === 'PAYMENT' ? 'bg-success/10 text-success-text' :
                          e.type === 'ADJUSTMENT' && e.balanceAfter <= e.balanceBefore
                            ? 'bg-info/10 text-info-text'
                            : 'bg-warning/10 text-warning-text'
                        }`}>{entryTypeLabel(e.type)}{e.type === 'ADJUSTMENT' ? (e.balanceAfter <= e.balanceBefore ? ' ↓' : ' ↑') : ''}</span>
                      </Td>
                      <Td className="text-muted-foreground text-xs" label={t('notes')}>
                        {e.sale?.invoiceNumber && (
                          <span className="block"><RecordLink type="invoice" id={e.sale.id} label={e.sale.invoiceNumber} variant="mono" /></span>
                        )}
                        {e.notes ?? (e.sale?.invoiceNumber ? null : '—')}
                        {e.type === 'PAYMENT' && (e.allocations?.length ?? 0) > 0 && (
                          <span className="block" data-testid="payment-allocations">
                            {t('paidInvoices', { defaultValue: 'Paid' })}:{' '}
                            {e.allocations!.map((a, n) => (
                              <span key={`${a.saleId ?? 'x'}-${n}`}>
                                {n > 0 && ', '}
                                {a.invoiceNumber ? <RecordLink type="invoice" id={a.saleId} label={a.invoiceNumber} variant="mono" /> : '—'} ({formatCurrency(a.amount)})
                              </span>
                            ))}
                          </span>
                        )}
                        {(e.paymentMethod || e.reference) && (
                          <span className="block">
                            {e.paymentMethod ? methodLabel(e.paymentMethod) : ''}
                            {e.paymentMethod && e.reference ? ' · ' : ''}
                            {e.reference ? `${t('refLabel')}: ${e.reference}` : ''}
                          </span>
                        )}
                      </Td>
                      <Td className="text-end text-destructive-text" label={t('charge')}>
                        {e.type === 'CHARGE' ? formatCurrency(e.amount) :
                         e.type === 'ADJUSTMENT' && e.balanceAfter > e.balanceBefore ? formatCurrency(e.amount) :
                         '—'}
                      </Td>
                      <Td className="text-end text-success-text" label={t('payment')}>
                        {e.type === 'PAYMENT' ? formatCurrency(e.amount) :
                         e.type === 'ADJUSTMENT' && e.balanceAfter <= e.balanceBefore ? formatCurrency(e.amount) :
                         '—'}
                      </Td>
                      <Td className="text-end font-medium" label={t('colBalance')}>{formatCurrency(e.balanceAfter)}</Td>
                    </Tr>
                  ))}
                </TBody>
              </Table>
            </div>
          )}
          {total > 20 && (
            <PaginationBar page={page} pageSize={20} total={total} onPageChange={setPage} />
          )}
        </CardContent>
      </Card>

      {/* Record Payment Dialog */}
      <Dialog header={t('recordPayment')} visible={paymentOpen} onHide={() => setPaymentOpen(false)} className="w-full max-w-xl">
        <div className="space-y-4 pt-2">
          {/* Outstanding balance banner */}
          <div className="rounded-lg bg-destructive/10 border border-destructive/30 p-3 flex items-center justify-between">
            <span className="text-sm text-destructive-text font-medium">{t('outstandingBalance')}</span>
            <span className="text-xl font-bold text-destructive-text">{formatCurrency(balance)}</span>
          </div>

          {/* Installment sync note */}
          {hasActiveInstallments && (
            <div className="rounded-lg bg-info/10 border border-info/30 p-3 text-xs text-info-text">
              {t('installmentSyncNote')}
            </div>
          )}

          {/* Amount */}
          <div>
            <Label>{t('amountReceivedPkr')} *</Label>
            <Input
              type="number" min="0.01" step="1"
              value={payForm.amount}
              onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
              placeholder="0"
              className="text-lg font-semibold h-11 mt-1"
              autoFocus
            />
            {/* POS-I134: clear the whole debt in one tap */}
            {balance > 0 && (
              <Button
                type="button" variant="outline" size="sm" className="mt-2"
                onClick={() => setPayForm(f => ({ ...f, amount: String(balance) }))}
              >
                {t('payFullBalance', { amount: formatCurrency(balance) })}
              </Button>
            )}
          </div>

          {/* POS-I135: optional manual invoice pick; otherwise oldest invoices are paid first */}
          {openInvoices.length > 0 && (
            <div className="rounded-lg border p-3 space-y-2">
              <p className="text-sm font-medium">{t('applyToInvoices', { defaultValue: 'Apply to invoices (optional)' })}</p>
              <p className="text-xs text-muted-foreground">{t('applyToInvoicesHint', { defaultValue: 'Leave empty and the payment clears the oldest unpaid invoices first. Enter an amount to put part of it on a specific invoice.' })}</p>
              <div className="space-y-1.5 max-h-44 overflow-y-auto">
                {openInvoices.filter((i) => i.saleId).map((i) => (
                  <div key={i.chargeEntryId} className="flex items-center gap-2 text-xs">
                    <span className="font-mono flex-1">{i.invoiceNumber ? <RecordLink type="invoice" id={i.saleId} label={i.invoiceNumber} variant="mono" className="text-xs" /> : null} <span className="text-muted-foreground">· {i.ageDays}d · {formatCurrency(i.open)}</span></span>
                    <Input
                      type="number" min="0" step="1" max={i.open}
                      className="h-8 w-28"
                      aria-label={`${t('applyToInvoices', { defaultValue: 'Apply to invoices (optional)' })} ${i.invoiceNumber}`}
                      value={invoicePick[i.saleId as string] ?? ''}
                      onChange={(e) => setInvoicePick((p) => ({ ...p, [i.saleId as string]: e.target.value }))}
                      placeholder="0"
                    />
                  </div>
                ))}
              </div>
              {pickExceeds && (
                <p className="text-xs text-destructive-text">{t('applyToInvoicesExceeds', { defaultValue: 'The invoice amounts are more than the payment or an invoice balance.' })}</p>
              )}
            </div>
          )}

          {/* Payment Method — POS-style single row grid */}
          <div>
            <Label className="mb-1.5 block">{t('paymentMethod')}</Label>
            <div className="grid grid-cols-5 gap-1.5">
              {PAYMENT_METHODS.filter(pm => (pm.value !== 'BANK_TRANSFER' || canUseBank) && (pm.value !== 'CHEQUE' || canUseCheques)).map(({ value, labelKey, icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setPayForm(f => ({ ...f, method: value, bankAccountId: '', cheque: emptyChequeForm(customer?.name ?? '') }))}
                  className={`flex flex-col items-center justify-center gap-1 rounded-lg border p-2 text-xs font-medium transition-all min-h-[64px] ${
                    payForm.method === value
                      ? 'border-primary bg-gradient-to-b from-primary/10 to-primary/10 text-primary-text'
                      : 'border-border hover:border-primary/50 hover:bg-muted/40'
                  }`}
                >
                  {icon}
                  <span className="leading-tight text-center">{t(labelKey)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Receiving bank account — every bank-settled method (hidden when <= 1 account) */}
          <BankAccountField
            method={payForm.method}
            accounts={bankAccounts}
            value={payForm.bankAccountId}
            onChange={(bankAccountId) => setPayForm(f => ({ ...f, bankAccountId }))}
          />

          {/* Reference / Transaction ID — non-cash, non-cheque methods */}
          {payForm.method !== 'CASH' && payForm.method !== 'CHEQUE' && (
            <div>
              <Label>{t('referenceTxn')}</Label>
              <Input
                value={payForm.reference}
                onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))}
                placeholder={t('referenceTxnPlaceholder')}
                className="mt-1"
              />
            </div>
          )}

          {/* Cheque particulars — creates a pending receivable cheque (T-480) */}
          {payForm.method === 'CHEQUE' && (
            <ChequeFields value={payForm.cheque} onChange={(cheque) => setPayForm(f => ({ ...f, cheque }))} />
          )}

          {/* Optional notes */}
          <div>
            <Label>{t('notesOptional')}</Label>
            <Input
              value={payForm.notes}
              onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))}
              placeholder={t('anyRemarks')}
              className="mt-1"
            />
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <Button variant="secondary" onClick={() => setPaymentOpen(false)}>{t('cancel')}</Button>
            <Button
              onClick={() => payMutation.mutate()}
              disabled={
                !payForm.amount || parseFloat(payForm.amount) <= 0 || payMutation.isPending || pickExceeds ||
                (payForm.method === 'CHEQUE' && !isChequeFormComplete(payForm.cheque))
              }
            >
              {payMutation.isPending ? t('recording') : t('recordPayment')}
            </Button>
          </div>
        </div>
      </Dialog>

      <PaymentReceiptView receipt={receipt} onClose={() => setReceipt(null)} />

      {/* Adjust Balance Dialog */}
      <Dialog header={t('adjustBalance')} visible={adjustOpen} onHide={() => setAdjustOpen(false)} className="w-full max-w-sm">
        <div className="space-y-4 pt-2">
          <p className="text-sm text-muted-foreground">{t('adjustmentHint')}</p>
          <div className="grid grid-cols-2 gap-2" role="group" aria-label={t('adjustmentDirection')}>
            {(['reduce', 'increase'] as const).map(dir => (
              <Button
                key={dir} type="button" size="sm"
                variant={adjForm.direction === dir ? 'default' : 'outline'}
                onClick={() => setAdjForm(f => ({ ...f, direction: dir }))}
              >
                {dir === 'reduce' ? t('adjustReduceDebt') : t('adjustIncreaseDebt')}
              </Button>
            ))}
          </div>
          <div>
            <Label>{t('adjustmentAmountPkr')}</Label>
            <Input type="number" min="0" step="1" placeholder={t('adjustmentPlaceholder')} value={adjForm.amount} onChange={e => setAdjForm(f => ({ ...f, amount: e.target.value.replace(/^-/, '') }))} />
            {adjAmountNum > 0 && (
              <p className={`mt-1 text-xs ${adjExceedsBalance ? 'text-destructive-text' : 'text-muted-foreground'}`}>
                {adjExceedsBalance
                  ? t('adjustExceedsBalance', { max: formatCurrency(balance) })
                  : t('adjustBalancePreview', { from: formatCurrency(balance), to: formatCurrency(adjNewBalance) })}
              </p>
            )}
          </div>
          <div>
            <Label>{t('reasonRequired')}</Label>
            <Input value={adjForm.notes} onChange={e => setAdjForm(f => ({ ...f, notes: e.target.value }))} />
            <div className="mt-2 flex flex-wrap gap-2">
              {(['adjustReasonWriteOff', 'adjustReasonCorrection', 'adjustReasonDiscount'] as const).map(key => (
                <Button key={key} type="button" variant="outline" size="sm" onClick={() => setAdjForm(f => ({ ...f, notes: t(key) }))}>
                  {t(key)}
                </Button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="secondary" onClick={() => setAdjustOpen(false)}>{t('cancel')}</Button>
            <Button onClick={() => adjMutation.mutate()} disabled={!(adjAmountNum > 0) || !adjForm.notes || adjExceedsBalance || adjMutation.isPending}>
              {adjMutation.isPending ? t('adjusting') : t('applyAdjustment')}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
